import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Collapse, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, InputAdornment, MenuItem,
  TextField, Tooltip, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/EditOutlined";
import SettingsIcon from "@mui/icons-material/SettingsOutlined";
import SearchIcon from "@mui/icons-material/Search";
import FacturasEditor from "./FacturasEditor";
import PlanillaFormDialog from "./PlanillaFormDialog";
import { eliminarPlanilla, enviarMasiva, listarPlanillas, mensajeError } from "../services/solicitudChequeService";

const num = (v) => { const n = Number(String(v ?? "").replace(",", ".")); return Number.isFinite(n) ? n : 0; };

// Mes anterior al actual: las planillas suelen ser servicios que se pagan vencidos (luz, agua…).
const mesAnterior = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return { mes: d.getMonth(), year: String(d.getFullYear()) };
};

// Solicitud masiva: se marcan varias planillas (servicios que se repiten cada mes), se completa el monto de cada una y se envían todas juntas.
export default function SolicitudMasivaDialog({ open, catalogos, puedeAdministrar, onClose, onEnviadas }) {
  const [planillas, setPlanillas] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState("");
  const [buscar, setBuscar] = useState("");
  const [sel, setSel] = useState({}); // id -> { monto, deposito, mes, year, obs, facturas }
  const [enviando, setEnviando] = useState(false);
  const [formPlanilla, setFormPlanilla] = useState({ abierto: false, planilla: null });
  const [aBorrar, setABorrar] = useState(null);
  const [admin, setAdmin] = useState(false); // modo administrar planillas (editar / eliminar), separado del modo de envío

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const { data } = await listarPlanillas();
      setPlanillas(Array.isArray(data) ? data : []);
      setError("");
    } catch (e) {
      setError(mensajeError(e, "No se pudieron cargar las planillas."));
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setSel({});
    setAdmin(false);
    setBuscar("");
    cargar();
  }, [open, cargar]);

  const visibles = useMemo(() => {
    const q = buscar.trim().toLowerCase();
    if (!q) return planillas;
    return planillas.filter((p) => [p.nombre, p.concepto, p.ciudad, p.obs, p.nro_contrato, p.nro_referencia, p.deposito].join(" ").toLowerCase().includes(q));
  }, [planillas, buscar]);

  const alternar = (p) => setSel((s) => {
    if (s[p.id]) { const { [p.id]: _quitar, ...resto } = s; return resto; }
    const { mes, year } = mesAnterior();
    return { ...s, [p.id]: { monto: p.monto || "", deposito: p.deposito || "", mes: catalogos.meses?.[mes] || "", year, obs: p.obs || "", facturas: [] } };
  });
  const cambiar = (id, campo, valor) => setSel((s) => ({ ...s, [id]: { ...s[id], [campo]: valor } }));

  const ids = Object.keys(sel);
  const faltantes = ids.filter((id) => !(num(sel[id].monto) > 0)).length;

  const enviar = async () => {
    setEnviando(true);
    setError("");
    try {
      const items = ids.map((id) => ({ plantilla_id: Number(id), ...sel[id] }));
      const { data } = await enviarMasiva(items);
      onEnviadas(data.ids.length);
    } catch (e) {
      setError(mensajeError(e, "No se pudieron enviar las solicitudes."));
    } finally {
      setEnviando(false);
    }
  };

  const borrar = async () => {
    try {
      await eliminarPlanilla(aBorrar.id);
      setSel((s) => { const { [aBorrar.id]: _q, ...resto } = s; return resto; });
      setABorrar(null);
      cargar();
    } catch (e) {
      setError(mensajeError(e, "No se pudo eliminar la planilla."));
      setABorrar(null);
    }
  };

  return (
    <Dialog open={open} onClose={enviando ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontSize: 17, fontWeight: 800, display: "flex", alignItems: "center", gap: 1 }}>
        <Box sx={{ flex: 1 }}>
          {admin ? "Administrar planillas" : "Solicitud masiva"}
          <Typography sx={{ fontSize: 12.5, color: "text.secondary", fontWeight: 400 }}>
            {admin ? "Crea, modifica o elimina las planillas. Aquí no se envían solicitudes." : "Marca las planillas que quieres pagar, completa el monto de cada una y envía todas juntas."}
          </Typography>
        </Box>
        {puedeAdministrar && admin && <Button size="small" variant="contained" startIcon={<AddIcon />} sx={{ textTransform: "none" }} onClick={() => setFormPlanilla({ abierto: true, planilla: null })}>Nueva planilla</Button>}
        {puedeAdministrar && !admin && <Button size="small" variant="outlined" startIcon={<SettingsIcon />} sx={{ textTransform: "none" }} onClick={() => { setSel({}); setAdmin(true); }}>Administrar planillas</Button>}
      </DialogTitle>

      <DialogContent dividers sx={{ bgcolor: "background.default" }}>
        {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError("")}>{error}</Alert>}
        <TextField
          size="small" fullWidth placeholder="Buscar por nombre, concepto, ciudad o contrato…" value={buscar} onChange={(e) => setBuscar(e.target.value)} sx={{ mb: 1.5 }}
          slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> } }}
        />
        {cargando && <Box sx={{ textAlign: "center", py: 3 }}><CircularProgress size={24} /></Box>}
        {!cargando && !visibles.length && <Typography sx={{ fontSize: 13, color: "text.secondary", py: 2 }}>No hay planillas para mostrar.</Typography>}

        <Box sx={{ display: "grid", gap: 1 }}>
          {visibles.map((p) => {
            const s = sel[p.id];
            return (
              <Box key={p.id} sx={{ border: 1, borderColor: s ? "primary.main" : "divider", borderRadius: 1.5, bgcolor: "background.paper", borderWidth: s ? 2 : 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, pr: 1 }}>
                  {admin ? <Box sx={{ width: 16 }} /> : <Checkbox checked={!!s} onChange={() => alternar(p)} inputProps={{ "aria-label": `Elegir ${p.nombre}` }} />}
                  <Box sx={{ flex: 1, minWidth: 0, py: 0.75, cursor: admin ? "default" : "pointer" }} onClick={() => !admin && alternar(p)}>
                    <Typography sx={{ fontSize: 13.5, fontWeight: 700 }} noWrap>{p.nombre}</Typography>
                    <Typography sx={{ fontSize: 12, color: "text.secondary" }} noWrap>
                      {p.concepto} · {p.ciudad}{p.nro_contrato ? ` · Contrato ${p.nro_contrato}` : ""}{p.nro_referencia ? ` · Ref. ${p.nro_referencia}` : ""}
                    </Typography>
                  </Box>
                  {p.moneda === "Dolares" && <Chip size="small" label="$us" color="info" />}
                  {puedeAdministrar && admin && (
                    <>
                      <Tooltip title="Modificar planilla"><IconButton size="small" onClick={() => setFormPlanilla({ abierto: true, planilla: p })}><EditIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Eliminar planilla"><IconButton size="small" onClick={() => setABorrar(p)}><DeleteOutlineIcon fontSize="small" /></IconButton></Tooltip>
                    </>
                  )}
                </Box>
                <Collapse in={!!s} unmountOnExit>
                  {s && (
                    <Box sx={{ px: 2, pb: 1.5, display: "grid", gap: 1.25 }}>
                      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "1fr 1fr 1fr 90px" }, gap: 1.25 }}>
                        <TextField
                          size="small" label="Monto" required value={s.monto} onChange={(e) => cambiar(p.id, "monto", e.target.value)} error={!(num(s.monto) > 0)}
                          slotProps={{ input: { startAdornment: <InputAdornment position="start">{p.moneda === "Dolares" ? "$us" : "Bs"}</InputAdornment> }, htmlInput: { inputMode: "decimal" } }}
                        />
                        <TextField size="small" label="Depositado a" value={s.deposito} onChange={(e) => cambiar(p.id, "deposito", e.target.value)} sx={{ gridColumn: { xs: "span 2", sm: "span 1" } }} />
                        <TextField size="small" select label="Mes del servicio" value={s.mes} onChange={(e) => cambiar(p.id, "mes", e.target.value)}>
                          {(catalogos.meses || []).map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                        </TextField>
                        <TextField size="small" label="Año" value={s.year} onChange={(e) => cambiar(p.id, "year", e.target.value)} slotProps={{ htmlInput: { inputMode: "numeric", maxLength: 4 } }} />
                      </Box>
                      <TextField size="small" label="Observación" value={s.obs} onChange={(e) => cambiar(p.id, "obs", e.target.value)} slotProps={{ htmlInput: { maxLength: 250 } }} />
                      <FacturasEditor facturas={s.facturas} onChange={(facturas) => cambiar(p.id, "facturas", facturas)} permitirVacio />
                    </Box>
                  )}
                </Collapse>
              </Box>
            );
          })}
        </Box>
      </DialogContent>

      <DialogActions>
        {admin ? (
          <>
            <Box sx={{ flex: 1 }} />
            <Button variant="contained" onClick={() => setAdmin(false)} sx={{ textTransform: "none", fontWeight: 700 }}>Listo, volver a la solicitud masiva</Button>
          </>
        ) : (
          <>
        <Typography sx={{ flex: 1, ml: 1, fontSize: 12.5, color: faltantes ? "warning.main" : "text.secondary" }}>
          {ids.length ? `${ids.length} seleccionada(s)${faltantes ? ` · falta el monto en ${faltantes}` : ""}` : "Ninguna seleccionada"}
        </Typography>
        <Button onClick={onClose} disabled={enviando} sx={{ textTransform: "none" }}>Cancelar</Button>
        <Button variant="contained" onClick={enviar} disabled={enviando || !ids.length || faltantes > 0} sx={{ textTransform: "none", fontWeight: 700 }}>
          {enviando ? <CircularProgress size={20} color="inherit" /> : `Enviar ${ids.length || ""} solicitud(es)`}
        </Button>
          </>
        )}
      </DialogActions>

      <PlanillaFormDialog
        open={formPlanilla.abierto} planilla={formPlanilla.planilla} catalogos={catalogos} onClose={() => setFormPlanilla({ abierto: false, planilla: null })}
        onGuardada={() => { setFormPlanilla({ abierto: false, planilla: null }); cargar(); }}
      />
      <Dialog open={!!aBorrar} onClose={() => setABorrar(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 17, fontWeight: 800 }}>Eliminar planilla</DialogTitle>
        <DialogContent><DialogContentText>¿Eliminar la planilla «{aBorrar?.nombre}»? Las solicitudes ya enviadas no se ven afectadas.</DialogContentText></DialogContent>
        <DialogActions>
          <Button onClick={() => setABorrar(null)} sx={{ textTransform: "none" }}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={borrar} sx={{ textTransform: "none", fontWeight: 700 }}>Eliminar</Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
